/**
 * RecordingCard Component
 * Display individual recording with thumbnail and metadata
 */

import { Card } from '@/ui/components/ui/card';
import { Button } from '@/ui/components/ui/button';
import { Badge } from '@/ui/components/ui/badge';
import { Play, Trash2, Clock, Calendar } from 'lucide-react';
import type { Recording } from '../types/recording';
import { useDeleteRecording } from '../hooks/useDeleteRecording';
import { useState } from 'react';

interface RecordingCardProps {
  recording: Recording;
  onPlay?: (recording: Recording) => void;
}

export function RecordingCard({ recording, onPlay }: RecordingCardProps) {
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);
  const deleteMutation = useDeleteRecording();

  const formatDuration = (seconds?: number) => {
    if (!seconds) return '--:--';
    const mins = Math.floor(seconds / 60);
    const secs = Math.floor(seconds % 60);
    return `${mins}:${secs.toString().padStart(2, '0')}`;
  };

  const formatDate = (date: Date) => {
    return new Intl.DateTimeFormat('en-US', {
      month: 'short',
      day: 'numeric',
      year: 'numeric',
    }).format(date);
  };

  const getStatusBadgeColor = () => {
    switch (recording.uploadStatus) {
      case 'ready':
        return 'bg-green-100 text-green-700';
      case 'processing':
        return 'bg-yellow-100 text-yellow-700';
      case 'uploading':
        return 'bg-purple-100 text-purple-700';
      case 'error':
        return 'bg-red-100 text-red-700';
      default:
        return 'bg-gray-100 text-gray-700';
    }
  };

  const getStatusLabel = () => {
    switch (recording.uploadStatus) {
      case 'ready':
        return 'Ready';
      case 'processing':
        return 'Processing';
      case 'uploading':
        return 'Uploading';
      case 'error':
        return 'Error';
      default:
        return recording.uploadStatus;
    }
  };

  const handleDelete = async () => {
    if (!showDeleteConfirm) {
      setShowDeleteConfirm(true);
      return;
    }

    try {
      await deleteMutation.mutateAsync(recording.id);
      setShowDeleteConfirm(false);
    } catch (error) {
      console.error('Failed to delete recording:', error);
    }
  };

  const thumbnailUrl = recording.thumbnailUrl || `https://image.mux.com/${recording.muxPlaybackId}/thumbnail.jpg?width=640&height=360&fit_mode=preserve`;

  return (
    <Card className="overflow-hidden hover:shadow-lg transition-shadow">
      {/* Thumbnail */}
      <div className="relative aspect-video bg-gray-900">
        {recording.thumbnailUrl || recording.muxPlaybackId ? (
          <img
            src={thumbnailUrl}
            alt={recording.title}
            className="w-full h-full object-cover"
            onError={(e) => {
              e.currentTarget.src = 'data:image/svg+xml,%3Csvg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 640 360"%3E%3Crect fill="%23374151" width="640" height="360"/%3E%3C/svg%3E';
            }}
          />
        ) : (
          <div className="w-full h-full flex items-center justify-center">
            <Play className="h-16 w-16 text-gray-600" />
          </div>
        )}

        {/* Status Badge */}
        <div className="absolute top-2 right-2">
          <Badge className={getStatusBadgeColor()}>
            {getStatusLabel()}
          </Badge>
        </div>

        {/* Duration Badge */}
        {recording.duration && (
          <div className="absolute bottom-2 right-2 bg-black/70 text-white text-xs px-2 py-1 rounded">
            {formatDuration(recording.duration)}
          </div>
        )}

        {/* Play Button Overlay */}
        {recording.uploadStatus === 'ready' && onPlay && (
          <button
            onClick={() => onPlay(recording)}
            className="absolute inset-0 flex items-center justify-center bg-black/0 hover:bg-black/30 transition-colors group"
          >
            <div className="w-16 h-16 rounded-full bg-white/90 flex items-center justify-center opacity-0 group-hover:opacity-100 transition-opacity">
              <Play className="h-8 w-8 text-gray-900 ml-1" />
            </div>
          </button>
        )}
      </div>

      {/* Content */}
      <div className="p-4">
        <h3 className="font-semibold text-gray-900 line-clamp-2 mb-2">
          {recording.title}
        </h3>

        {recording.description && (
          <p className="text-sm text-gray-600 line-clamp-2 mb-3">
            {recording.description}
          </p>
        )}

        {/* Metadata */}
        <div className="flex items-center gap-4 text-xs text-gray-500 mb-3">
          <div className="flex items-center gap-1">
            <Calendar className="h-3 w-3" />
            <span>{formatDate(recording.uploadedAt)}</span>
          </div>
          {recording.duration && (
            <div className="flex items-center gap-1">
              <Clock className="h-3 w-3" />
              <span>{formatDuration(recording.duration)}</span>
            </div>
          )}
        </div>

        {/* Actions */}
        <div className="flex gap-2">
          {recording.uploadStatus === 'ready' && onPlay && (
            <Button
              size="sm"
              onClick={() => onPlay(recording)}
              className="flex-1 gap-2"
            >
              <Play className="h-4 w-4" />
              Play
            </Button>
          )}
          <Button
            size="sm"
            variant={showDeleteConfirm ? 'destructive' : 'outline'}
            onClick={handleDelete}
            disabled={deleteMutation.isPending}
            className="gap-2"
          >
            <Trash2 className="h-4 w-4" />
            {showDeleteConfirm ? 'Confirm' : 'Delete'}
          </Button>
        </div>

        {showDeleteConfirm && (
          <p className="text-xs text-red-600 mt-2">
            Click Delete again to confirm
          </p>
        )}
      </div>
    </Card>
  );
}
